/**
 * Historique du navigateur (window.history) pour les écrans hors espace de travail (connexion, retour SSO).
 * L'application connectée n'a pas de BrowserRouter : chaque page ouverte a son propre routeur (pages/espace.tsx)
 * et l'espace de travail synchronise lui-même l'URL avec la page active.
 */
import React from 'react';
import { createPath, parsePath, Router, type Navigator, type To } from 'react-router-dom';
import { ROUTE_VERIFICATION_RETOUR } from '@agora/shared';

const abonnes = new Set<() => void>();
const notifier = () => abonnes.forEach((f) => f());

function abonner(f: () => void): () => void {
  abonnes.add(f);
  window.addEventListener('popstate', f);
  return () => {
    abonnes.delete(f);
    window.removeEventListener('popstate', f);
  };
}

export const cheminDe = (to: To): string => (typeof to === 'string' ? to : createPath(to));

/** Adresse courante : chemin + paramètres + ancre. */
export const adresseCourante = (): string => window.location.pathname + window.location.search + window.location.hash;

/** Écrans publics (hors session) : connexion, retour du SSO, retour de la fenêtre de vérification d'identité. */
export const estRouteAuth = (chemin: string): boolean =>
  chemin === '/connexion' || chemin.startsWith('/auth/') || chemin === ROUTE_VERIFICATION_RETOUR;

export const navigateurGlobal: Navigator = {
  createHref: (to) => cheminDe(to),
  push: (to, state) => { window.history.pushState(state ?? null, '', cheminDe(to)); notifier(); },
  replace: (to, state) => { window.history.replaceState(state ?? null, '', cheminDe(to)); notifier(); },
  go: (n) => window.history.go(n),
};

export function naviguerNavigateur(chemin: string, remplacer = false): void {
  if (remplacer) navigateurGlobal.replace(chemin);
  else navigateurGlobal.push(chemin);
}

/** true sur /connexion et /auth/… (ne se met à jour que lorsque cette valeur change). */
export function useRouteAuth(): boolean {
  return React.useSyncExternalStore(abonner, () => estRouteAuth(window.location.pathname));
}

/** Routeur lié à l'historique du navigateur, pour les écrans hors session. */
export function RouteurNavigateur({ children }: { children: React.ReactNode }) {
  const adresse = React.useSyncExternalStore(abonner, adresseCourante);
  const location = React.useMemo(() => ({ ...parsePath(adresse), state: window.history.state, key: adresse }), [adresse]);
  return React.createElement(Router, { location, navigator: navigateurGlobal, future: { v7_relativeSplatPath: true } }, children);
}
